import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DatabaseBackup, RotateCcw } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import StepUpDialog from './StepUpDialog';
import { demoFullReset, demoReset, demoResetStatus } from './api';
import { useBrand } from '../brand/BrandProvider';
import { apiAllowed, useExperience } from '../experience/experience';

/** GET /api/review/demo-reset: an owner/admin/manager on a demo tenant gets allowed=true; everything else hides.
 * A client flavour (features.demo_controls: false) never shows them and never asks. */
export function useDemoTools() {
  const { features } = useBrand();
  const exp = useExperience();
  // pipeline_api.DEMO_RESET_ROLES: the server still decides whether this is a demo tenant.
  const enabled = features.demo_controls !== false
    && apiAllowed(exp, '/api/review/demo-reset')
    && (exp.roles || []).some((role) => ['org_owner', 'org_admin', 'manager'].includes(role));
  const organizationId = exp.organizationId;
  const [info, setInfo] = useState(null);
  const request = useRef(0);
  const refresh = useCallback(async () => {
    const current = ++request.current;
    if (!enabled) { setInfo(null); return null; }
    try {
      const body = await demoResetStatus();
      if (request.current === current) setInfo({ organizationId, body: body && body.allowed === true ? body : null });
      return body;
    } catch {
      if (request.current === current) setInfo(null);
      return null;
    }
  }, [enabled, organizationId]);
  useEffect(() => {
    setInfo(null);
    refresh();
    return () => { request.current += 1; };
  }, [refresh]);
  return { info: enabled && info?.organizationId === organizationId ? info?.body : null, refresh };
}

const btnCls = 'inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl border border-surface-border text-ink-muted hover:text-ink hover:bg-surface-sunken text-sm';

/** Both resets are high impact: confirm what it does, then a second check (StepUpDialog) before it runs. */
export default function DemoResetButton({ info, onDone, pollMs = 4000 }) {
  const [open, setOpen] = useState(null);       // 'intake' | 'fresh' | null: the "what this does" step
  const [stepUp, setStepUp] = useState(null);   // 'intake' | 'fresh' | null: the "confirm it is you" step
  const [loading, setLoading] = useState(false);

  // While fresh data is loading, ask until the seeder reports it is no longer pending.
  useEffect(() => {
    if (!loading || info?.allowed !== true) return undefined;
    const t = setInterval(async () => {
      try {
        const body = await demoResetStatus();
        if (!body?.full_reset?.pending) { setLoading(false); onDone?.(); }
      } catch { /* keep asking */ }
    }, pollMs);
    return () => clearInterval(t);
  }, [loading, info?.allowed, onDone, pollMs]);

  if (!info) return null;
  const n = info.invoices ?? 0;
  const fresh = info.full_reset?.available === true;
  const pending = loading || info.full_reset?.pending === true;
  const next = (kind) => { setOpen(null); setStepUp(kind); };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" data-testid="reset-demo-btn" onClick={() => setOpen('intake')} className={btnCls} disabled={pending}>
        <RotateCcw className="w-4 h-4" /> Reset demo <span className="text-ink-subtle">({n} uploaded)</span>
      </button>
      {fresh && (
        <button type="button" data-testid="fresh-demo-btn" onClick={() => setOpen('fresh')} className={btnCls} disabled={pending}>
          <DatabaseBackup className="w-4 h-4" /> {pending ? 'Loading fresh demo data…' : 'Fresh demo data'}
        </button>
      )}
      <ConfirmDialog open={open === 'intake'} title="Reset the demo?" testid="reset-demo-dialog" danger
        confirmLabel="Continue" onCancel={() => setOpen(null)} onConfirm={() => next('intake')}>
        <p>This removes {n} uploaded or emailed invoice{n === 1 ? '' : 's'}, their review tasks and any prices they wrote, so you can upload the same files again.</p>
        {(info.to_archive ?? 0) > 0 && (
          <p data-testid="reset-archive-note">
            The {info.to_archive} posted invoice{info.to_archive === 1 ? '' : 's'} from the simulated wholesaler move out of the lists
            so you start from an empty page. Their price history stays, so costs and price changes are unaffected.
          </p>
        )}
        <p>The invoice mailbox is checked again from scratch on its next run, so emailed invoices come back in.</p>
        <p>Seed data is kept. This cannot be undone.</p>
      </ConfirmDialog>
      <ConfirmDialog open={open === 'fresh'} title="Load fresh demo data?" testid="fresh-demo-dialog" danger
        confirmLabel="Continue" onCancel={() => setOpen(null)} onConfirm={() => next('fresh')}>
        <p>Everything in this demo goes back to how it was seeded: sites, menus, stock, orders, prices, invoices, the mailbox record and anything changed since.</p>
        <p>It takes a few seconds, or about 3 minutes when there is no snapshot from today. Everyone using the demo sees the change.</p>
      </ConfirmDialog>
      <StepUpDialog open={stepUp === 'intake'} action="demo_reset" testid="reset-step-up"
        summary={`Reset the demo: remove ${n} invoice${n === 1 ? '' : 's'} and what they changed.`}
        perform={(token) => demoReset(token)} onCancel={() => setStepUp(null)}
        onDone={() => { setStepUp(null); onDone?.(); }} />
      <StepUpDialog open={stepUp === 'fresh'} action="demo_fresh_data" testid="fresh-step-up"
        summary="Load fresh demo data: the whole demo goes back to its seeded state."
        perform={(token) => demoFullReset(token)} onCancel={() => setStepUp(null)}
        onDone={() => { setStepUp(null); setLoading(true); }} />
    </div>
  );
}
